import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { Container, Section, Title, Text, Button } from '../styles/components';
import CoagulationVisualization from '../components/coagulation/CoagulationVisualization';

// Styled components for the About page
const HeroSection = styled.section`
  position: relative;
  padding: 4rem 0;
  overflow: hidden;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  
  @media (min-width: 768px) {
    padding: 6rem 0;
  }
`;

const HeroOverlay = styled.div`
  position: absolute;
  inset: 0;
  background-color: black;
  opacity: 0.5;
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 10;
  text-align: center;
`;

const MissionTag = styled.span`
  display: inline-block;
  padding: 0.5rem 1rem;
  background-color: var(--red-50);
  color: var(--primary);
  border-radius: 9999px;
  font-size: 0.875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 1.5rem;
`;

const HeroTitle = styled.h1`
  font-size: 2.25rem;
  font-weight: 800;
  color: white;
  margin-bottom: 1.5rem;
  line-height: 1.2;
  
  @media (min-width: 768px) {
    font-size: 3.75rem;
  }
`;

const HeroText = styled.p`
  font-size: 1.125rem;
  color: var(--gray-100);
  max-width: 48rem;
  margin: 0 auto 2rem;
  line-height: 1.7;
  
  @media (min-width: 768px) {
    font-size: 1.25rem;
  }
`;

const Divider = styled.div`
  width: 3rem;
  height: 0.25rem;
  background-color: var(--primary);
  margin: 0 auto 2rem;
  border-radius: 9999px;
`;

// Process section styled components
const ProcessContainer = styled.div`
  position: relative;
`;

const TimelineLine = styled.div`
  position: absolute;
  top: 6rem;
  left: 0;
  right: 0;
  height: 0.25rem;
  background-color: var(--gray-100);
  display: none;
  
  @media (min-width: 768px) {
    display: block;
  }
`;

const ProcessGrid = styled.div`
  display: grid;
  gap: 2rem;
  position: relative;
  z-index: 10;
  
  @media (min-width: 768px) {
    grid-template-columns: repeat(4, 1fr);
  }
`;

const ProcessCard = styled.div<{ borderColor: string }>`
  background-color: white;
  border-radius: 0.75rem;
  padding: 1.5rem;
  text-align: center;
  border: 1px solid ${props => props.borderColor};
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  
  &:hover {
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
  }
`;

const ProcessIcon = styled.div<{ bgColor: string, borderColor: string }>`
  width: 5rem;
  height: 5rem;
  background-color: white;
  border-radius: 9999px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.5rem;
  border: 3px solid ${props => props.borderColor};
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;

const StepTag = styled.div<{ bgColor: string }>`
  display: inline-block;
  padding: 0.25rem 0.75rem;
  background-color: ${props => props.bgColor};
  color: white;
  font-size: 0.75rem;
  font-weight: 700;
  border-radius: 9999px;
  margin-bottom: 1rem;
`;

const ProcessTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--gray-900);
  margin-bottom: 0.75rem;
`;

const ProcessText = styled.p`
  color: var(--gray-600);
  line-height: 1.5;
`;

const ProcessCallout = styled.div`
  background-color: var(--gray-50);
  padding: 2rem;
  border-radius: 0.75rem;
  max-width: 64rem;
  margin: 4rem auto 0;
  text-align: center;
  border: 1px solid var(--red-100);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
`;

const CalloutTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--primary);
  margin-bottom: 1rem;
`;

const CalloutText = styled.p`
  color: var(--gray-700);
  line-height: 1.7;
  font-size: 1.125rem;
`;

// A Nod to Tradition section styled components
const TraditionImage = styled.img`
  width: 100%;
  height: 16rem;
  object-fit: cover;
  border-radius: 0.75rem;
  margin-bottom: 4rem;
`;

const PullQuote = styled.div`
  position: relative;
  max-width: 64rem;
  margin: 0 auto 4rem;
  background-color: var(--gray-50);
  padding: 2rem;
  border-radius: 0.75rem;
  text-align: center;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);

  &::before {
    content: '"';
    position: absolute;
    top: 0;
    left: 1rem;
    color: var(--primary);
    opacity: 0.1;
    font-size: 6rem;
    font-family: serif;
    line-height: 1;
  }
`;

const QuoteText = styled.p`
  font-size: 1.25rem;
  font-weight: 300;
  color: var(--gray-700);
  font-style: italic;
  position: relative;
  z-index: 10;
  line-height: 1.7;

  @media (min-width: 768px) {
    font-size: 1.5rem;
  }
`;

const TraditionGrid = styled.div`
  display: grid;
  gap: 3rem;
  max-width: 80rem;
  margin: 0 auto;

  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const TraditionColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const TraditionTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--gray-900);
  margin-bottom: 0.5rem;
`;

const TraditionDivider = styled.div`
  width: 2.5rem;
  height: 0.25rem;
  background-color: var(--primary);
  border-radius: 9999px;
  margin-bottom: 1.5rem;
`;

const TraditionText = styled.p`
  color: var(--gray-700);
  line-height: 1.7;
  margin-bottom: 1.5rem;

  strong {
    font-weight: 600;
  }
`;

const TraditionImageSmall = styled.img`
  width: 100%;
  height: 20rem;
  object-fit: cover;
  border-radius: 0.75rem;
  margin: 0 auto;
`;

const TraditionBox = styled.div`
  background-color: var(--blue-50);
  padding: 2rem;
  border-radius: 0.75rem;
  position: relative;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 25%;
    height: 0.25rem;
    background-color: var(--blue-400);
    border-radius: 0 9999px 0 0;
  }
`;

const YearTag = styled.div`
  display: inline-block;
  padding: 0.25rem 0.75rem;
  background-color: var(--gray-200);
  color: var(--gray-800);
  font-size: 0.875rem;
  font-weight: 600;
  border-radius: 9999px;
  margin-bottom: 1rem;
`;

// The Ota Difference section styled components
const DifferenceSection = styled(Section)`
  background-color: var(--gray-50);
`;

const DifferenceGrid = styled.div`
  display: grid;
  gap: 2rem;
  max-width: 80rem;
  margin: 0 auto;

  @media (min-width: 768px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const DifferenceCard = styled.div`
  background-color: white;
  border-radius: 0.75rem;
  padding: 2rem;
  text-align: center;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  }
`;

const DifferenceIcon = styled.div<{ bgColor: string }>`
  width: 4rem;
  height: 4rem;
  background-color: ${props => props.bgColor};
  border-radius: 9999px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.5rem;
`;

const DifferenceTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--gray-900);
  margin-bottom: 1rem;
`;

const DifferenceText = styled.p`
  color: var(--gray-700);
  line-height: 1.7;
`;

const DifferenceLink = styled(Link)`
  display: inline-block;
  padding: 0.75rem 2rem;
  background-color: var(--primary);
  color: white;
  font-weight: 600;
  text-decoration: none;
  border-radius: 0.5rem;
  transition: all 0.3s ease;

  &:hover {
    background-color: var(--gray-700);
    transform: translateY(-1px);
  }
`;

const ToggleButton = styled.button`
  display: inline-flex;
  align-items: center;
  padding: 0.75rem 2rem;
  background-color: var(--primary);
  color: white;
  font-weight: 600;
  border: none;
  border-radius: 0.5rem;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background-color: var(--gray-700);
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`;

// Video section styled components
const VideoContainer = styled.div`
  max-width: 64rem;
  margin: 0 auto;
  aspect-ratio: 16/9;
  border-radius: 0.75rem;
  overflow: hidden;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;

const VideoFrame = styled.iframe`
  width: 100%;
  height: 100%;
  border: none;
`;

// CTA section styled components
const CTASection = styled(Section)`
  background-color: var(--primary);
  color: white;
`;

const CTATitle = styled.h2`
  font-size: 1.875rem;
  font-weight: 800;
  text-align: center;
  margin-bottom: 1.5rem;
  line-height: 1.2;

  @media (min-width: 768px) {
    font-size: 2.25rem;
  }
`;

const CTAText = styled.p`
  font-size: 1.25rem;
  max-width: 48rem;
  margin: 0 auto 2.5rem;
  text-align: center;
  line-height: 1.7;
`;

const CTAButton = styled(Link)`
  display: inline-block;
  padding: 0.75rem 2rem;
  background-color: white;
  color: var(--primary);
  font-weight: 600;
  text-decoration: none;
  border-radius: 0.5rem;
  transition: all 0.3s ease;

  &:hover {
    background-color: var(--gray-100);
    transform: translateY(-1px);
  }
`;

const About: React.FC = () => {
  const [showCoagulation, setShowCoagulation] = useState(false);

  return (
    <div>
      {/* Hero Section */}
      <HeroSection style={{ backgroundImage: "url('/images/about.webp')" }}>
        <HeroOverlay />
        <Container>
          <HeroContent>
            <MissionTag>OUR MISSION</MissionTag>
            <HeroTitle>Preserving Tradition, Crafting Excellence</HeroTitle>
            <HeroText>
              Our mission is to create the finest tofu possible by honoring time-tested traditional methods. 
              Since 1911, we are committed to crafting exceptional tofu that respects both heritage and artisanal excellence.
            </HeroText>
          </HeroContent>
        </Container>
      </HeroSection>

      {/* Our Process Section */}
      <Section>
        <Container>
          <Title>OUR PROCESS</Title>
          <Divider />
          <Text style={{ textAlign: 'center', maxWidth: '48rem', margin: '0 auto 4rem' }}>
            At Ota Tofu, we believe that exceptional tofu comes from exceptional care.
            Each step in our process reflects our commitment to quality, tradition, and craftsmanship.
          </Text>

          {/* Process Steps Timeline */}
          <ProcessContainer>
            <TimelineLine />
            
            <ProcessGrid>
              {/* Step 1 */}
              <ProcessCard borderColor="var(--purple-200)">
                <ProcessIcon bgColor="white" borderColor="var(--purple-400)">
                  <svg xmlns="http://www.w3.org/2000/svg" width="2.5rem" height="2.5rem" fill="none" viewBox="0 0 24 24" stroke="currentColor" style={{ color: 'var(--purple-400)' }}>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                  </svg>
                </ProcessIcon>
                <StepTag bgColor="var(--purple-400)">Step 1</StepTag>
                <ProcessTitle>Premium Ingredients</ProcessTitle>
                <ProcessText>
                  By starting with Ohio-grown, high-protein soybeans, we can achieve the best texture, taste, and nutritional content of our tofu.
                </ProcessText>
              </ProcessCard>

              {/* Step 2 */}
              <ProcessCard borderColor="var(--red-200)">
                <ProcessIcon bgColor="white" borderColor="var(--red-400)">
                  <svg xmlns="http://www.w3.org/2000/svg" width="2.5rem" height="2.5rem" fill="none" viewBox="0 0 24 24" stroke="currentColor" style={{ color: 'var(--red-400)' }}>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </ProcessIcon>
                <StepTag bgColor="var(--red-400)">Step 2</StepTag>
                <ProcessTitle>Patience & Precision</ProcessTitle>
                <ProcessText>
                  The soybeans are left to thoroughly soak in water until they are fully saturated, slowing down the cooking process to maximize the tofu's texture.
                </ProcessText>
              </ProcessCard>

              {/* Step 3 */}
              <ProcessCard borderColor="var(--orange-200)">
                <ProcessIcon bgColor="white" borderColor="var(--orange-400)">
                  <svg xmlns="http://www.w3.org/2000/svg" width="2.5rem" height="2.5rem" fill="none" viewBox="0 0 24 24" stroke="currentColor" style={{ color: 'var(--orange-400)' }}>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                  </svg>
                </ProcessIcon>
                <StepTag bgColor="var(--orange-400)">Step 3</StepTag>
                <ProcessTitle>Expert Attention</ProcessTitle>
                <ProcessText>
                  Many factors can affect the consistency, so we take extra care during grinding, filtering, coagulating, and pressing to offer a consistent product.
                </ProcessText>
              </ProcessCard>

              {/* Step 4 */}
              <ProcessCard borderColor="var(--blue-200)">
                <ProcessIcon bgColor="white" borderColor="var(--blue-400)">
                  <svg xmlns="http://www.w3.org/2000/svg" width="2.5rem" height="2.5rem" fill="none" viewBox="0 0 24 24" stroke="currentColor" style={{ color: 'var(--blue-400)' }}>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                  </svg>
                </ProcessIcon>
                <StepTag bgColor="var(--blue-400)">Step 4</StepTag>
                <ProcessTitle>Handcrafted Care</ProcessTitle>
                <ProcessText>
                  By processing the tofu by hand, we can ensure that each block of tofu that bears the Ota Tofu logo is of the highest possible quality.
                </ProcessText>
              </ProcessCard>
            </ProcessGrid>
          </ProcessContainer>

          {/* Process Callout */}
          <ProcessCallout>
            <CalloutTitle>Traditional Meets Modern</CalloutTitle>
            <CalloutText>
              While we maintain the traditional methods that have defined our quality for over a century, we continuously refine our process to craft the perfect tofu—a balance of heritage and innovation that you can taste in every bite.
            </CalloutText>
          </ProcessCallout>
        </Container>
      </Section>

      {/* A Nod to Tradition Section */}
      <Section>
        <Container>
          <Title>A NOD TO TRADITION</Title>
          <Divider />

          {/* Feature Image */}
          <TraditionImage src="/images/machines.webp" alt="Original Ota Tofu Building" />

          {/* Pull Quote */}
          <PullQuote>
            <QuoteText>
              Tofu is a soy bean curd that dates back more than a thousand years, from ancient China to Japan, and eventually to America where Ota Tofu has proudly carried this tradition since 1911.
            </QuoteText>
          </PullQuote>

          {/* Two Column Content */}
          <TraditionGrid>
            {/* Left Column */}
            <TraditionColumn>
              <TraditionTitle>The Oldest Tofu in America</TraditionTitle>
              <TraditionDivider />

              <TraditionText>
                When the Ota family started their business in 1911, it provided a healthy taste of home for Portland's thriving and rapidly growing Japanese immigrant community. <strong>Ota Tofu is now the oldest producer of tofu in the United States</strong> and one of the oldest in North America.
              </TraditionText>
              <TraditionText>
                The first Ota Tofu blocks were cooked in gas-fired brick ovens and except for a four-year period during World War Two, they have been a mainstay of the Asian-American community ever since.
              </TraditionText>

              {/* Historic Shop Image */}
              <TraditionImageSmall src="/images/building2.webp" alt="Original Ota Tofu Building" />
            </TraditionColumn>

            {/* Right Column */}
            <TraditionColumn>
              <TraditionBox>
                <TraditionTitle>Perseverance Through Adversity</TraditionTitle>
                <TraditionText>
                  During the war, the Ota family, along with tens of thousands of Japanese-Americans along the west coast, were forced to give up their homes and businesses. Fortunately, a sympathetic landlord protected their original location in Northwest Portland and at the end of the war the Ota's were able to resume producing Tofu for Portland's Asian-American community.
                </TraditionText>
              </TraditionBox>

              <div>
                <YearTag>2019</YearTag>
                <TraditionTitle>Continuing the Legacy</TraditionTitle>
                <TraditionDivider />

                <TraditionText>
                  When Jason Ogata, a Portland-native and former professional baseball player who grew up eating Ota Tofu, learned that the Ota family was thinking about closing their shop, it was clear what needed to be done. Jason took over in 2019, working alongside the Ota family to keep America's oldest tofu company alive.
                </TraditionText>
              </div>
            </TraditionColumn>
          </TraditionGrid>
        </Container>
      </Section>

      {/* The Ota Difference Section */}
      <DifferenceSection>
        <Container>
          <Title>THE OTA DIFFERENCE</Title>
          <Divider />
          <Text style={{ textAlign: 'center', maxWidth: '48rem', margin: '0 auto 4rem' }}>
            What makes Ota Tofu stand out from other brands? Our dedication to traditional craftsmanship and quality ingredients creates a tofu experience unlike any other.
          </Text>

          <DifferenceGrid>
            {/* Factor 1 */}
            <DifferenceCard>
              <DifferenceIcon bgColor="var(--blue-50)">
                <svg xmlns="http://www.w3.org/2000/svg" width="2rem" height="2rem" fill="none" viewBox="0 0 24 24" stroke="currentColor" style={{ color: 'var(--blue-400)' }}>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2 10c1.5-2 3.5-2 5 0 1.5 2 3.5 2 5 0 1.5-2 3.5-2 5 0 1.5 2 3.5 2 5 0" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2 14c1.5-2 3.5-2 5 0 1.5 2 3.5 2 5 0 1.5-2 3.5-2 5 0 1.5 2 3.5 2 5 0" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2 18c1.5-2 3.5-2 5 0 1.5 2 3.5 2 5 0 1.5-2 3.5-2 5 0 1.5 2 3.5 2 5 0" />
                </svg>
              </DifferenceIcon>
              <DifferenceTitle>Traditional Coagulant</DifferenceTitle>
              <DifferenceText>
                Ota Tofu is made with only three ingredients – soybeans, water, and nigari. This traditional coagulant, derived from seawater and imported from Japan, is one reason our tofu must be handmade.
              </DifferenceText>
            </DifferenceCard>

            {/* Factor 2 */}
            <DifferenceCard>
              <DifferenceIcon bgColor="var(--red-50)">
                <svg xmlns="http://www.w3.org/2000/svg" width="2rem" height="2rem" fill="none" viewBox="0 0 24 24" stroke="currentColor" style={{ color: 'var(--red-400)' }}>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 11.5V14m0-2.5v-6a1.5 1.5 0 113 0m-3 6a1.5 1.5 0 00-3 0v2a7.5 7.5 0 0015 0v-5a1.5 1.5 0 00-3 0m-6-3V11m0-5.5v-1a1.5 1.5 0 013 0v1m0 0V11m0-5.5a1.5 1.5 0 013 0v3m0 0V11" />
                </svg>
              </DifferenceIcon>
              <DifferenceTitle>Delicate Process</DifferenceTitle>
              <DifferenceText>
                Nigari produces soybean curds that are too delicate for a machine to process, requiring each slab of Ota Tofu to be hand stirred and handcrafted.
              </DifferenceText>
            </DifferenceCard>

            {/* Factor 3 */}
            <DifferenceCard>
              <DifferenceIcon bgColor="var(--purple-50)">
                <svg xmlns="http://www.w3.org/2000/svg" width="2rem" height="2rem" fill="none" viewBox="0 0 24 24" stroke="currentColor" style={{ color: 'var(--purple-400)' }}>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                </svg>
              </DifferenceIcon>
              <DifferenceTitle>Superior Result</DifferenceTitle>
              <DifferenceText>
                While other producers use calcium sulfate (gypsum) that allows for machine processing, we believe nigari creates better texture and flavor – and invite you to taste the difference.
              </DifferenceText>
            </DifferenceCard>
          </DifferenceGrid>

          <div style={{ textAlign: 'center', marginTop: '3rem' }}>
            <ToggleButton onClick={() => setShowCoagulation(!showCoagulation)}>
              {showCoagulation ? 'Hide Coagulation Science' : 'More about Coagulation Science'}
            </ToggleButton>
          </div>

          {showCoagulation && (
            <div className="mt-8">
              <CoagulationVisualization />
            </div>
          )}
        </Container>
      </DifferenceSection>

      {/* Video Section */}
      <Section>
        <Container>
          <Title>OUR STORY</Title>
          <Divider />
          <Text style={{ textAlign: 'center', maxWidth: '48rem', margin: '0 auto 3rem' }}>
            Discover the story behind America's oldest tofu shop and our commitment to preserving traditional tofu-making methods.
          </Text>

          <VideoContainer>
            <VideoFrame
              src="https://www.youtube.com/embed/Ki0BUon0zIE"
              title="Ota Tofu Story"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </VideoContainer>
        </Container>
      </Section>

      {/* CTA Section */}
      <CTASection>
        <Container>
          <CTATitle>Experience the Taste of Tradition</CTATitle>
          <CTAText>
            Ready to taste what makes Ota Tofu special? Find our products at local markets throughout Portland and the Pacific Northwest.
          </CTAText>
          <div style={{ textAlign: 'center' }}>
            <CTAButton to="/#faq-1">Find Where to Buy</CTAButton>
          </div>
        </Container>
      </CTASection>
    </div>
  );
};

export default About; 